import React, { useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import ImageCropEditor from './ImageCropEditor';
import '../../styles/ImagePickerModal.css';

const FIELD_TABS = [
  { key: 'vertical', label: 'Poster', column: 'vertical_image' },
  { key: 'horizontal', label: 'Thumbnail', column: 'horizontal_image' },
];

const SOURCE_TABS = [
  { key: 'recommended', label: 'Recommended' },
  { key: 'history', label: 'History' },
  { key: 'all', label: 'All' },
  { key: 'custom', label: 'Custom' },
];

// fetch()'s res.json() throws an opaque "unexpected character" error whenever the server (or a
// proxy in front of it) returns something that isn't JSON - e.g. an HTML error/redirect page.
// Parse defensively so failures surface a readable message instead.
const parseJsonResponse = async (res) => {
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (e) {
    throw new Error(`Server returned an unexpected response (${res.status} ${res.statusText}): ${text.slice(0, 200) || 'empty body'}`);
  }
  if (!res.ok && !data.error) {
    throw new Error(`Request failed (${res.status} ${res.statusText})`);
  }
  return data;
};

// Modal for picking a poster/thumbnail image for a media item: switch between the poster and
// thumbnail slot, then choose an image from this item's own seasons ("Recommended"), search
// across every image in the library ("All"), or upload/reuse a custom image ("Custom").
const ImagePickerModal = ({ isOpen, onClose, mediaItem, apiBaseUrl, onImageSelected, onSeasonUpdate, initialField = 'vertical', seasonId = null, seasonLabel = null }) => {
  const [activeField, setActiveField] = useState(initialField);
  const [activeTab, setActiveTab] = useState('recommended');
  const [searchQuery, setSearchQuery] = useState('');
  const [images, setImages] = useState([]);
  const [currentPath, setCurrentPath] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [cropSubmitting, setCropSubmitting] = useState(false);
  const [statusNote, setStatusNote] = useState(null);
  const fileInputRef = useRef(null);

  const fieldColumn = FIELD_TABS.find((f) => f.key === activeField)?.column || 'vertical_image';

  const activeSeason = useMemo(() => {
    if (!seasonId || !Array.isArray(mediaItem?.seasons)) return null;
    return mediaItem.seasons.find((s) => s.id === seasonId) || null;
  }, [mediaItem?.seasons, seasonId]);

  const seasonTargets = useMemo(() => {
    if (!Array.isArray(mediaItem?.seasons)) return [];
    return mediaItem.seasons.map((s) => ({
      id: s.id,
      label: s.title_en || s.title_mal || s.title_tmdb || `Season ${s.id}`,
    }));
  }, [mediaItem?.seasons]);

  useEffect(() => {
    if (isOpen) {
      setActiveField(initialField);
      setActiveTab('recommended');
      setSearchQuery('');
      setError(null);
      setEditingImage(null);
      setStatusNote(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialField, mediaItem?.id]);

  const fetchImages = useMemo(
    () =>
      debounce((field, tab, query) => {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({ type: field, tab });
        if (query) params.set('q', query);
        if (seasonId) params.set('season_id', seasonId);
        fetch(`${apiBaseUrl}/media/${mediaItem.id}/image_options?${params.toString()}`, {
          credentials: 'include',
        })
          .then(parseJsonResponse)
          .then((data) => {
            if (data.error) throw new Error(data.error);
            setImages(data.images || []);
            setCurrentPath(data.current || null);
          })
          .catch((err) => setError(err.message || 'Failed to load images'))
          .finally(() => setLoading(false));
      }, 300),
    [apiBaseUrl, mediaItem?.id, seasonId]
  );

  useEffect(() => {
    if (!isOpen || !mediaItem?.id) return;
    fetchImages(activeField, activeTab, searchQuery);
  }, [isOpen, mediaItem?.id, activeField, activeTab, searchQuery, fetchImages]);

  if (!isOpen) return null;

  const selectImage = (path) => {
    setCurrentPath(path);
    fetch(`${apiBaseUrl}/media/${mediaItem.id}/select_image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ type: activeField, path, season_id: seasonId }),
    })
      .then(parseJsonResponse)
      .then((data) => {
        if (data.success) {
          if (seasonId) {
            onSeasonUpdate?.(data.season);
          } else {
            onImageSelected?.(fieldColumn, data.media);
          }
        } else {
          setError(data.error || 'Failed to set image');
        }
      })
      .catch((err) => setError(err.message || 'Failed to set image'));
  };

  const uploadFile = (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('image_type', activeField);
    setUploading(true);
    setError(null);
    fetch(`${apiBaseUrl}/media/${mediaItem.id}/upload_custom_image`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })
      .then(parseJsonResponse)
      .then((data) => {
        if (data.success) {
          setImages((prev) => [data.image, ...prev]);
          selectImage(data.image.path);
        } else {
          setError(data.error || 'Failed to upload image');
        }
      })
      .catch((err) => setError(err.message || 'Failed to upload image'))
      .finally(() => setUploading(false));
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    uploadFile(file);
  };

  const handlePaste = (e) => {
    if (activeTab !== 'custom') return;
    const item = Array.from(e.clipboardData?.items || []).find((it) => it.type.startsWith('image/'));
    if (!item) return;
    e.preventDefault();
    uploadFile(item.getAsFile());
  };

  const handlePasteButtonClick = async () => {
    if (!navigator.clipboard?.read) {
      setError('Clipboard access not supported here - click into this area and press Ctrl+V instead.');
      return;
    }
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const clipboardItem of clipboardItems) {
        const imageType = clipboardItem.types.find((t) => t.startsWith('image/'));
        if (imageType) {
          const blob = await clipboardItem.getType(imageType);
          uploadFile(new File([blob], `pasted.${imageType.split('/')[1] || 'png'}`, { type: imageType }));
          return;
        }
      }
      setError('No image found on the clipboard.');
    } catch (err) {
      setError('Could not read clipboard - press Ctrl+V instead.');
    }
  };

  const handleEditClick = (e, image) => {
    e.stopPropagation();
    setError(null);
    setEditingImage(image);
  };

  const handleCropCreate = ({ imageType, crop, seasonId: cropSeasonId }) => {
    setCropSubmitting(true);
    setError(null);
    fetch(`${apiBaseUrl}/media/${mediaItem.id}/crop_image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ source_path: editingImage.path, image_type: imageType, crop, season_id: cropSeasonId }),
    })
      .then(parseJsonResponse)
      .then((data) => {
        if (!data.success) {
          setError(data.error || 'Failed to create cropped image');
          return;
        }
        if (cropSeasonId) {
          onSeasonUpdate?.(data.season);
          setStatusNote(`Applied to ${seasonTargets.find((s) => s.id === cropSeasonId)?.label || 'season'}.`);
        } else {
          const targetColumn = FIELD_TABS.find((f) => f.key === imageType)?.column;
          onImageSelected?.(targetColumn, data.media);
          setStatusNote(null);
        }
        setEditingImage(null);
        setActiveField(imageType);
        setActiveTab('custom');
        fetchImages(imageType, 'custom', '');
      })
      .catch((err) => setError(err.message || 'Failed to create cropped image'))
      .finally(() => setCropSubmitting(false));
  };

  const previewUrl = activeSeason ? activeSeason[fieldColumn] : mediaItem?.[fieldColumn];

  return (
    <div className="image-picker-overlay" onClick={onClose}>
      <div className="image-picker-content" onClick={(e) => e.stopPropagation()} onPaste={handlePaste}>
        {!editingImage && (
          <div className="image-picker-header">
            <div className="image-picker-field-tabs">
              {FIELD_TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={`image-picker-field-tab ${activeField === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveField(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <button className="image-picker-close" onClick={onClose} aria-label="Close">
              &times;
            </button>
          </div>
        )}

        {seasonLabel && <div className="image-picker-subtitle">Editing: {seasonLabel}</div>}

        {!editingImage && (
          <div className={`image-picker-preview ${activeField}`}>
            {previewUrl ? (
              <img src={previewUrl} alt="Current" />
            ) : (
              <div className="image-picker-preview-placeholder">No image selected</div>
            )}
          </div>
        )}

        {editingImage ? (
          <ImageCropEditor
            image={editingImage}
            initialAspect={activeField}
            initialTarget={seasonId}
            seasonTargets={seasonTargets}
            submitting={cropSubmitting}
            error={error}
            onCancel={() => { setEditingImage(null); setError(null); }}
            onCreate={handleCropCreate}
          />
        ) : (
          <>
            <div className="image-picker-source-tabs">
              {SOURCE_TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={`image-picker-source-tab ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'all' && (
              <input
                type="text"
                className="image-picker-search"
                placeholder="Search by title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            )}

            {statusNote && <div className="image-picker-status">{statusNote}</div>}
            {error && <div className="image-picker-error">{error}</div>}

            <div className="image-picker-scroll">
              {activeTab === 'custom' && (
                <>
                  <button className="image-picker-upload-tile" onClick={handleUploadClick} disabled={uploading}>
                    <span className="image-picker-upload-icon">+</span>
                    <span>{uploading ? 'Uploading...' : 'Upload new'}</span>
                  </button>
                  <button className="image-picker-upload-tile image-picker-paste-tile" onClick={handlePasteButtonClick} disabled={uploading}>
                    <svg className="image-picker-upload-icon" viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
                      <path d="M9 2a1 1 0 0 0-1 1v1H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2V3a1 1 0 0 0-1-1H9zm1 2h4v2h-4V4zM6 6h2v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V6h2v14H6V6z" />
                    </svg>
                    <span>Paste from clipboard</span>
                  </button>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />

              {loading ? (
                <div className="image-picker-status">Loading...</div>
              ) : images.length === 0 ? (
                activeTab !== 'custom' && <div className="image-picker-status">No images found</div>
              ) : (
                images.map((image) => (
                  <button
                    key={image.id}
                    className={`image-picker-tile ${currentPath === image.path ? 'selected' : ''}`}
                    onClick={() => selectImage(image.path)}
                    title={image.label}
                  >
                    <img src={image.url} alt={image.label} />
                    {currentPath === image.path && (
                      <span className="image-picker-checkmark">&#10003;</span>
                    )}
                    <button className="image-picker-edit-btn" onClick={(e) => handleEditClick(e, image)}>Edit</button>
                    <span className="image-picker-tile-label">{image.label}</span>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImagePickerModal;

